import React, { useState, useEffect } from 'react';
import { Search, Eye, Trash2, Edit, AlertCircle, DollarSign, MessageSquare } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [search]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = search 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?search=${encodeURIComponent(search)}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch user details');
      const data = await response.json();
      setSelectedUser(data);
      setShowDetails(true);
    } catch (err) {
      console.error(err);
    }
  };

  const updateFreeQuestions = async (userId, newAmount) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/free-questions?free_questions=${newAmount}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Failed to update');
      alert('Количество бесплатных вопросов обновлено!');
      fetchUsers();
      if (showDetails) viewUserDetails(userId);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Вы уверены? Это действие нельзя отменить!')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      alert('Пользователь удален');
      fetchUsers();
      setShowDetails(false);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  return (
    <div>
      {/* Search */}
      <div className="mb-6 relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по email или имени..."
          className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>

      {/* Users Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Пользователь</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Бесплатных</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Всего вопросов</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Платежи</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">Загрузка...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">Пользователи не найдены</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-900/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-medium">{user.name}</div>
                        <div className="text-sm text-gray-400">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.free_questions_left > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.free_questions_left}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{user.total_questions_asked}</td>
                    <td className="px-6 py-4 text-gray-300">{user.total_payments.toFixed(0)} ₽</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewUserDetails(user.id)}
                          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                          title="Просмотр"
                        >
                          <Eye className="w-4 h-4 text-gray-400" />
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Details Modal */}
      {showDetails && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">Детали пользователя</h2>
              <button onClick={() => setShowDetails(false)} className="text-gray-400 hover:text-white">
                ✕
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-white mb-3">Информация</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Email:</span> <span className="text-white">{selectedUser.user.email}</span></div>
                  <div><span className="text-gray-400">Имя:</span> <span className="text-white">{selectedUser.user.name}</span></div>
                  <div><span className="text-gray-400">Телефон:</span> <span className="text-white">{selectedUser.user.phone || '-'}</span></div>
                  <div><span className="text-gray-400">Дата рождения:</span> <span className="text-white">{selectedUser.user.date_of_birth || '-'}</span></div>
                </div>
              </div>

              {/* Free Questions Manager */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-white mb-3">Управление бесплатными вопросами</h3>
                <div className="flex items-center gap-4">
                  <span className="text-gray-400">Текущее количество:</span>
                  <span className="text-2xl font-bold text-white">{selectedUser.user.free_questions_left || 0}</span>
                  <button
                    onClick={() => updateFreeQuestions(selectedUser.user.id, (selectedUser.user.free_questions_left || 0) + 1)}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                  >
                    +1
                  </button>
                  <button
                    onClick={() => updateFreeQuestions(selectedUser.user.id, 3)}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    Сбросить (3)
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-sm mb-1">Сообщений</div>
                  <div className="text-2xl font-bold text-white">{selectedUser.stats.total_messages}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-sm mb-1">Сессий</div>
                  <div className="text-2xl font-bold text-white">{selectedUser.stats.total_sessions}</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-gray-400 text-sm mb-1">Записей</div>
                  <div className="text-2xl font-bold text-white">{selectedUser.stats.total_records}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;